/**
 * Hook for managing push notifications.
 *
 * Handles:
 * - Detecting whether push is supported on this device
 * - Exposing a `shouldPromptForPermission` flag for banner UIs
 * - Requesting notification permission on explicit user action (button click)
 * - Registering the resulting FCM token with the backend (with platform metadata)
 * - Listening for foreground messages and invalidating notification queries
 *
 * IMPORTANT: This hook no longer auto-requests permission. Browsers increasingly
 * penalise unsolicited permission prompts — especially on Android PWA — so the
 * request must be driven by an explicit user gesture (see `EnableNotificationsBanner`).
 *
 * IMPORTANT: inert for the external customer portal — see `CLIENT_AUDIENCE_DISABLED`.
 */

import { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useUpdateFcmToken } from '@/hooks/api/useAuth';
import {
  getMessagingToken,
  onForegroundMessage,
  isFirebaseConfigured,
} from '@/lib/firebase';
import { detectPlatform } from '@/utils/platform';
import { TokenStorage } from '@/api/interceptors';
import { UserRole } from '@/types';
import { useQueryClient } from '@tanstack/react-query';

interface UsePushNotificationsResult {
  /** Whether push notifications are supported on this browser/device */
  isSupported: boolean;
  /** Whether push permission has already been granted */
  isPermissionGranted: boolean;
  /**
   * Whether the UI should prompt the user to enable push notifications.
   * True when: authenticated, supported, permission is in `default` state,
   * and the user has not opted out via profile settings (`isPushEnabled`).
   */
  shouldPromptForPermission: boolean;
  /** Request push notification permission (must be called from a user gesture) */
  requestPermission: () => Promise<boolean>;
  /** Whether we're currently requesting permission */
  isRequesting: boolean;
}

/**
 * What the hook reports for the external customer portal: push does not exist there in
 * Р1, so nothing is supported, nothing prompts, and `requestPermission()` refuses
 * without ever touching the browser's permission API.
 *
 * The portal has no `fcm-token` endpoint at all — `ClientAuthController` was built
 * without one on purpose (decision Р12 in the edition of 2026-08-18, contract
 * r1-api-contract.md §8). Left ungated, a customer who accepted the permission prompt
 * would immediately POST to a route that answers 404.
 *
 * Turning push back on for customers is three edits, none of them here: two endpoints on
 * `ClientAuthController` (`POST fcm-token`, `POST fcm-token/logout`), flipping
 * `IsPushEnabled` in `CustomerUsersService.CreateCustomerUserAsync` (it hard-codes
 * `false` today), and dropping this constant with its guards. The internal audience is
 * untouched by all of it — including the push toggle in the profile screen's
 * `PersonalInfoTab`, which is internal-only.
 */
const CLIENT_AUDIENCE_DISABLED: UsePushNotificationsResult = {
  isSupported: false,
  isPermissionGranted: false,
  shouldPromptForPermission: false,
  requestPermission: async () => false,
  isRequesting: false,
};

/**
 * Hook to manage push notifications lifecycle.
 */
export function usePushNotifications(): UsePushNotificationsResult {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const updateFcmToken = useUpdateFcmToken();
  const queryClient = useQueryClient();

  // Role once the user object is loaded; the stored audience covers the window before it
  // arrives (session restore on reload), when `user` is still null.
  const isClientAudience =
    user?.role === UserRole.Client || TokenStorage.getAudience() === 'client';

  const [isSupported, setIsSupported] = useState(false);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const hasAutoRegisteredRef = useRef(false);

  // Check if push notifications are supported
  useEffect(() => {
    if (isClientAudience) {
      setIsSupported(false);
      setIsPermissionGranted(false);
      return;
    }

    const supported =
      'Notification' in window &&
      'serviceWorker' in navigator &&
      isFirebaseConfigured();

    setIsSupported(supported);

    if (supported) {
      setIsPermissionGranted(Notification.permission === 'granted');
    }
  }, [isClientAudience]);

  // Derive whether the banner should prompt the user.
  // Uses isPermissionGranted (reactive) instead of reading Notification.permission inline
  // so the memo re-computes when the user grants permission via requestPermission().
  const shouldPromptForPermission = useMemo(() => {
    if (isClientAudience) return false;
    if (!isAuthenticated || !isSupported) return false;
    if (typeof Notification === 'undefined') return false;
    if (isPermissionGranted || Notification.permission === 'denied') return false;
    // Respect user's opt-out from profile settings
    return user?.isPushEnabled !== false;
  }, [isAuthenticated, isSupported, user?.isPushEnabled, isPermissionGranted, isClientAudience]);

  // Request permission and get FCM token.
  // Must be triggered by a user gesture (button click).
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (isClientAudience) {
      // No push for the customer portal in Р1 — never open the browser prompt.
      return false;
    }

    if (!isSupported) {
      console.warn('Push notifications are not supported');
      return false;
    }

    setIsRequesting(true);

    try {
      const token = await getMessagingToken();

      // Sync React state with the browser's actual permission regardless of
      // whether a token came back. Without this, an edge case where permission
      // is `granted` but `getToken()` returned null (SW activation race on
      // Android PWA, transient Firebase error, stale VAPID key) would leave
      // `isPermissionGranted` at `false` forever — and the EnableNotifications
      // banner would stay visible because its `shouldPromptForPermission` memo
      // only recomputes when this state flips.
      if (typeof Notification !== 'undefined') {
        setIsPermissionGranted(Notification.permission === 'granted');
      }

      if (token) {
        // Update token on backend (only if authenticated)
        if (isAuthenticated) {
          try {
            await updateFcmToken.mutateAsync({
              fcmToken: token,
              platform: detectPlatform(),
              userAgent: navigator.userAgent,
            });
            console.log('FCM token updated on backend');
          } catch (error) {
            console.error('Failed to update FCM token on backend:', error);
          }
        }

        return true;
      }

      return false;
    } finally {
      setIsRequesting(false);
    }
  }, [isSupported, isAuthenticated, updateFcmToken, isClientAudience]);

  // Auto re-register the FCM token on app load when permission is already
  // `granted`. Without this, a user who previously enabled notifications never
  // re-posts their token to the backend (the banner is hidden in `granted`
  // state), so a server-side wipe of `DeviceTokens` — or Firebase rotating the
  // token — would silently break push delivery until the next login.
  // Fire-and-forget; failures are logged but never surfaced.
  useEffect(() => {
    if (!isAuthenticated || isClientAudience) {
      hasAutoRegisteredRef.current = false;
      return;
    }
    if (!isSupported || !isPermissionGranted) return;
    if (hasAutoRegisteredRef.current) return;
    hasAutoRegisteredRef.current = true;

    (async () => {
      try {
        const token = await getMessagingToken();
        if (!token) return;
        await updateFcmToken.mutateAsync({
          fcmToken: token,
          platform: detectPlatform(),
          userAgent: navigator.userAgent,
        });
      } catch (error) {
        console.warn('[usePushNotifications] Auto re-register failed:', error);
      }
    })();
  }, [isAuthenticated, isSupported, isPermissionGranted, updateFcmToken, isClientAudience]);

  // Listen for foreground messages
  useEffect(() => {
    if (isClientAudience) return;
    if (!isAuthenticated || !isSupported || !isPermissionGranted) {
      return;
    }

    const unsubscribe = onForegroundMessage(() => {
      // Don't call showBrowserNotification() here — Firebase handles
      // browser notifications via the Notification field in background.
      // In foreground, SignalR delivers the notification in-app.
      // Showing a browser notification here would cause duplicates.

      // Refresh the feed and both unread counts — `['notifications']` is the prefix of every
      // notification key, the unread count included (there never was an `['unreadCount']` key).
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isAuthenticated, isSupported, isPermissionGranted, queryClient, isClientAudience]);

  // Returned after every hook above has run — the guards inside them already keep the
  // client audience inert; this makes the reported shape unambiguous and stable.
  if (isClientAudience) {
    return CLIENT_AUDIENCE_DISABLED;
  }

  return {
    isSupported,
    isPermissionGranted,
    shouldPromptForPermission,
    requestPermission,
    isRequesting,
  };
}
